import React, { useState, useCallback } from 'react';
import { useQuery, useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import studentService from '../../services/api/student';
import { useSettings } from '../../contexts/SettingsContext';
import { 
  Upload, 
  CheckCircle, 
  AlertCircle, 
  User, 
  CreditCard,
  MessageCircle,
  ChevronLeft,
  ShieldCheck,
  Zap,
  BookOpen,
  Hash,
  Image as ImageIcon,
  X,
  Smartphone,
  ArrowRight,
  Info
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';

const PaymentConfirmation = () => {
  const navigate = useNavigate();
  const { openWhatsApp, getSetting } = useSettings();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [formData, setFormData] = useState({
    studentName: '',
    phone: '',
    courseId: '',
    transactionNumber: '',
    paymentMethod: 'bank_transfer', // Default payment method
    amount: 0, // Will be set based on course
    durationDays: 30, // Will be set based on course default_duration_days
    screenshot: null
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [fileSizeInfo, setFileSizeInfo] = useState(null);
  const toast = useToast();

  // تحميل طرق الدفع من الإعدادات
  React.useEffect(() => {
    const methods = [
      { 
        label: 'فودافون كاش', 
        number: getSetting('vodafone_cash_number', '01012345678'), 
        color: 'bg-red-50 text-red-600 border-red-100' 
      },
      { 
        label: 'إنستاباي (InstaPay)', 
        number: getSetting('instapay_id', 'munasati@instapay'), 
        color: 'bg-purple-50 text-purple-600 border-purple-100' 
      },
      { 
        label: 'أورانج كاش', 
        number: getSetting('orange_cash_number', '01212345678'), 
        color: 'bg-orange-50 text-orange-600 border-orange-100' 
      }
    ];
    setPaymentMethods(methods);
  }, [getSetting]);

  // جلب الكورسات المتاحة (المنطق الأصلي)
  const { data: courses, isLoading } = useQuery(
    ['available-courses', { purchasable: true }],
    () => studentService.getCourses({ purchasable: true }).then(data => {
      return data?.data?.courses || data?.courses || data || [];
    })
  );

  // جلب طلبات الدفع الخاصة بالمستخدم لعرضها ومنع التكرار
  // (تمت إزالة عرض الطلبات السابقة من واجهة تأكيد الدفع)

  // تنفيذ عملية إرسال طلب الدفع (Mutation)
  const paymentMutation = useMutation(
    async (data) => {
      const formDataToSend = new FormData();
      Object.keys(data).forEach(key => {
        if (key === 'screenshot' && data[key]) {
          formDataToSend.append('screenshot', data[key]);
        } else if (key !== 'screenshot') {
          // Use camelCase to match backend validation
          formDataToSend.append(key, data[key]);
        }
      });
      
      const response = await studentService.submitPaymentRequest(formDataToSend);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('تم استلام طلبك بنجاح! سيتم تفعيل الكورس خلال 24 ساعة كحد أقصى.');
        setFormData({
          studentName: '',
          phone: '',
          courseId: '',
          transactionNumber: '',
          paymentMethod: 'bank_transfer',
          amount: 0,
          screenshot: null
        });
        setPreviewImage(null);
      },
      onError: (error) => {
        if (error.response?.data?.details) {
          error.response.data.details.forEach(detail => {
            toast.error(`${detail.param}: ${detail.msg}`);
          });
        } else {
          toast.error(error.response?.data?.error || 'عذراً، فشل إرسال الطلب، يرجى المحاولة لاحقاً.');
        }
      }
    }
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Auto-set amount and duration when course is selected
    if (name === 'courseId') {
      const selectedCourse = courses?.find(c => c.id === parseInt(value));
      if (selectedCourse) {
        setFormData(prev => ({ 
          ...prev, 
          courseId: value,
          amount: selectedCourse.price || 0,
          durationDays: selectedCourse.default_duration_days || 30
        }));
      }
    }
  };

  // دالة ضغط الصورة بشكل ذكي
  const compressImage = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          
          // تقليل الحجم إذا كان أكبر من 1920x1440
          const maxWidth = 1920;
          const maxHeight = 1440;
          
          if (width > maxWidth || height > maxHeight) {
            const ratio = Math.min(maxWidth / width, maxHeight / height);
            width *= ratio;
            height *= ratio;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);
          
          // ضغط الصورة بجودة متدرجة حتى نصل إلى أقل من 1 MB
          let quality = 0.9;
          
          const tryCompress = () => {
            canvas.toBlob(
              (blob) => {
                
                if (blob.size > 1024 * 1024 && quality > 0.3) {
                  // إذا كان الحجم أكبر من 1 MB، قلل الجودة
                  quality -= 0.1;
                  tryCompress();
                } else if (blob.size > 5 * 1024 * 1024) {
                  // إذا تجاوز 5 MB حتى بأقل جودة، رفض
                  reject(new Error('الصورة كبيرة جداً حتى بعد الضغط'));
                } else {
                  // نجح الضغط
                  const compressedFileWithName = new File([blob], file.name, {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                  });
                  
                  resolve({
                    file: compressedFileWithName,
                    preview: canvas.toDataURL('image/jpeg', Math.max(quality, 0.5)),
                    originalSize: file.size,
                    compressedSize: blob.size,
                    ratio: ((1 - blob.size / file.size) * 100).toFixed(1)
                  });
                }
              },
              'image/jpeg',
              quality
            );
          };
          
          tryCompress();
        };
        
        img.onerror = () => reject(new Error('فشل تحميل الصورة'));
        img.src = e.target.result;
      };
      
      reader.onerror = () => reject(new Error('خطأ في قراءة الملف'));
      reader.readAsDataURL(file);
    });
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // التحقق من نوع الملف
    if (!file.type.startsWith('image/')) {
      toast.error('يرجى اختيار ملف صورة صالح (JPG, PNG)');
      return;
    }

    // إذا كانت الصورة أكبر من 1 MB، اضغطها
    if (file.size > 1 * 1024 * 1024) {
      setIsCompressing(true);
      
      compressImage(file)
        .then((result) => {
          setFormData(prev => ({ ...prev, screenshot: result.file }));
          setPreviewImage(result.preview);
          setFileSizeInfo({
            original: (result.originalSize / 1024 / 1024).toFixed(2),
            compressed: (result.compressedSize / 1024 / 1024).toFixed(2),
            ratio: result.ratio
          });
          
          toast.success(
            `تم ضغط الصورة بنجاح! من ${(result.originalSize / 1024 / 1024).toFixed(2)} MB إلى ${(result.compressedSize / 1024 / 1024).toFixed(2)} MB`
          );
        })
        .catch((error) => {
          toast.error(error.message || 'فشل ضغط الصورة، يرجى المحاولة مع صورة أخرى');
        })
        .finally(() => {
          setIsCompressing(false);
        });
    } else {
      // إذا كانت أقل من 1 MB، لا تحتاج ضغط
      setFormData(prev => ({ ...prev, screenshot: file }));

      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
        setFileSizeInfo({
          original: (file.size / 1024).toFixed(0),
          compressed: (file.size / 1024).toFixed(0),
          ratio: '0'
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.studentName.trim()) return toast.error('يرجى إدخال اسم الطالب بالكامل');
    if (!formData.phone.match(/^01[0-2,5]\d{8}$/)) return toast.error('رقم الهاتف غير صحيح، يرجى إدخال رقم موبايل مصري');
    if (!formData.courseId) return toast.error('يرجى اختيار الكورس المراد تفعيله');
    if (!formData.transactionNumber.trim()) return toast.error('رقم العملية مطلوب للتحقق من الدفع');
    if (!formData.screenshot) return toast.error('يرجى إرفاق صورة إثبات التحويل');

    paymentMutation.mutate(formData);
  };

  const sendViaWhatsApp = () => {
    const course = courses?.find(c => c.id === parseInt(formData.courseId));
    const subjectName = course?.subject_name || 'مادة غير محدد';
    const courseTitle = course?.title || 'كورس غير محدد';
    const message = `*طلب تفعيل كورس*\n\n👤 *الاسم:* ${formData.studentName}\n📱 *الموبايل:* ${formData.phone}\n📖 *المادة:* ${subjectName}\n📚 *الكورس:* ${courseTitle}\n🔢 *رقم العملية:* ${formData.transactionNumber}\n\n_(مرفق صورة التحويل)_`;
    openWhatsApp(message);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري تهيئة بوابة الدفع...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-10 px-4 sm:px-6 py-10 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-in fade-in slide-in-from-top duration-500">
        <div className="space-y-1">
            <button
                onClick={() => navigate('/')}
                className="group flex items-center text-gray-400 hover:text-primary-600 font-bold transition-all mb-2 text-sm"
            >
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                العودة للرئيسية
            </button>
            <h1 className="text-3xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                <div className="bg-primary-600 text-white p-1.5 rounded-xl"><ShieldCheck size={24} fill="white" /></div>
                تأكيد عملية الدفع
            </h1>
            <p className="text-gray-500 font-medium">أرسل بيانات التحويل ليتم فتح محتوى الكورس في حسابك</p>
        </div>
      </div>

      {/* Payment Methods Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {paymentMethods.map((method, idx) => (
            <div key={idx} className={`p-5 rounded-3xl border-2 border-dashed ${method.color} relative overflow-hidden group`}>
                <div className="relative z-10">
                    <p className="text-[10px] font-black uppercase tracking-widest opacity-70 mb-1">{method.label}</p>
                    <p className="text-sm font-black font-mono tracking-wider">{method.number}</p>
                </div>
                <Zap className="absolute -bottom-2 -left-2 w-12 h-12 opacity-10 -rotate-12 group-hover:scale-125 transition-transform" />
            </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left Column: Confirmation Form */}
        <div className="lg:col-span-7 space-y-6">
            {/* (إشعار طلبات سابقة محذوف من واجهة تأكيد الدفع حسب طلب المستخدم) */}
          <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-12 relative group">
            <div className="absolute top-0 left-0 w-2 h-full bg-primary-600 opacity-20 group-hover:opacity-100 transition-opacity"></div>
            
            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Student Name */}
                <div className="space-y-2">
                    <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                        <User size={14} className="text-primary-500" /> الاسم الكامل للطالب *
                    </label>
                    <input
                        type="text"
                        name="studentName"
                        value={formData.studentName}
                        onChange={handleInputChange}
                        className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900"
                        placeholder="أحمد محمد علي..."
                        required
                    />
                </div>

                {/* Phone */}
                <div className="space-y-2">
                    <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                        <Smartphone size={14} className="text-primary-500" /> رقم الموبايل الشخصي *
                    </label>
                    <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900 text-left"
                        placeholder="01xxxxxxxxx"
                        required
                    />
                </div>
              </div>

              {/* Course Selection */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                    <BookOpen size={14} className="text-primary-500" /> الكورس المراد الاشتراك فيه *
                </label>
                <div className="relative">
                    <select
                        name="courseId"
                        value={formData.courseId}
                        onChange={handleInputChange}
                        className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-700 appearance-none cursor-pointer"
                        required
                    >
                        <option value="">اختر الكورس من القائمة...</option>
                        {courses?.map(course => {
                            const durationText = course.default_duration_days === 30 ? 'اشتراك شهري' :
                                              course.default_duration_days === 120 ? 'اشتراك ترم دراسي' :
                                              course.default_duration_days === 300 ? 'اشتراك عام دراسي' :
                                              `${course.default_duration_days} يوم`;
                            return (
                              <option key={course.id} value={course.id}>
                                {course.title} - ({course.price} ج.م) - ({durationText})
                              </option>
                            );
                          })}
                    </select>
                    <ChevronLeft size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 -rotate-90 pointer-events-none" />
                </div>
              </div>

              {/* Transaction Number */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                    <Hash size={14} className="text-primary-500" /> رقم العملية الإشعار *
                </label>
                <input
                    type="text"
                    name="transactionNumber"
                    value={formData.transactionNumber}
                    onChange={handleInputChange}
                    className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white focus:border-primary-500 transition-all font-bold text-gray-900"
                    placeholder="رقم المرجع المذكور في رسالة التحويل"
                    required
                />
              </div>

              {/* Enhanced Screenshot Upload */}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">
                    <ImageIcon size={14} className="text-primary-500" /> إثبات التحويل (سكرين شوت) *
                </label>
                <div className={`relative border-4 border-dashed rounded-[2.5rem] p-8 text-center transition-all group/upload ${previewImage ? 'bg-gray-50 border-emerald-100' : 'bg-gray-50/50 border-gray-100 hover:border-primary-200 hover:bg-primary-50/30'}`}>
                    {isCompressing ? (
                        <div className="space-y-6 py-8">
                            <div className="flex justify-center">
                                <div className="w-12 h-12 rounded-full border-4 border-gray-200 border-t-primary-600 animate-spin"></div>
                            </div>
                            <div>
                                <p className="text-sm font-black text-gray-900 mb-2">جاري ضغط الصورة...</p>
                                <p className="text-xs text-gray-500">قد يستغرق بعض الثواني</p>
                            </div>
                        </div>
                    ) : previewImage ? (
                        <div className="space-y-6">
                            <img src={previewImage} alt="Preview" className="mx-auto max-h-56 rounded-3xl shadow-2xl ring-8 ring-white" />
                            
                            {/* File Size Info */}
                            {fileSizeInfo && (fileSizeInfo.ratio !== '0' || fileSizeInfo.compressed > 1024) && (
                                <div className="bg-white rounded-2xl p-4 border border-green-100">
                                    <div className="grid grid-cols-3 gap-2 text-center">
                                        <div>
                                            <p className="text-[10px] text-gray-500 font-bold uppercase">قبل</p>
                                            <p className="text-sm font-black text-gray-900">{fileSizeInfo.original > 1024 ? (fileSizeInfo.original / 1024).toFixed(2) : fileSizeInfo.original} {fileSizeInfo.original > 1024 ? 'MB' : 'KB'}</p>
                                        </div>
                                        <div className="flex items-center justify-center">
                                            <ArrowRight className="w-4 h-4 text-gray-400 rotate-180" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] text-gray-500 font-bold uppercase">بعد</p>
                                            <p className="text-sm font-black text-emerald-600">{fileSizeInfo.compressed} KB</p>
                                        </div>
                                    </div>
                                    {fileSizeInfo.ratio !== '0' && (
                                        <div className="mt-3 pt-3 border-t border-gray-200">
                                            <p className="text-xs font-bold text-emerald-600">✓ تم تقليل الحجم بنسبة {fileSizeInfo.ratio}%</p>
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            <button
                                type="button"
                                onClick={() => { setPreviewImage(null); setFormData(prev => ({ ...prev, screenshot: null })); setFileSizeInfo(null); }}
                                className="bg-red-50 text-red-600 px-6 py-2 rounded-xl font-bold text-xs hover:bg-red-600 hover:text-white transition-all flex items-center gap-2 mx-auto"
                            >
                                <X size={14} /> إزالة الصورة واختيار أخرى
                            </button>
                        </div>
                    ) : (
                        <label htmlFor="screenshot-input" className="cursor-pointer block">
                            <div className="bg-white w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm text-gray-400 group-hover/upload:scale-110 group-hover/upload:text-primary-600 transition-all">
                                <Upload size={32} />
                            </div>
                            <p className="text-sm font-black text-gray-900 mb-1">اضغط لرفع صورة التحويل</p>
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">PNG, JPG - سيتم ضغط الصور الأكبر من 1 MB تلقائياً</p>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileChange}
                                disabled={isCompressing}
                                className="hidden"
                                id="screenshot-input"
                                required
                            />
                        </label>
                    )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                <button
                    type="submit"
                    disabled={paymentMutation.isLoading}
                    className="bg-primary-600 text-white py-5 rounded-[2rem] font-black text-lg shadow-2xl shadow-primary-100 hover:bg-primary-700 transition-all transform active:scale-95 disabled:opacity-50 flex items-center justify-center gap-3"
                >
                    {paymentMutation.isLoading ? (
                    <>
                        <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin" />
                        جاري الإرسال...
                    </>
                    ) : (
                    <>
                        <CheckCircle size={22} fill="white" />
                        إتمام الطلب
                    </>
                    )}
                </button>

                <button
                    type="button"
                    onClick={sendViaWhatsApp}
                    className="bg-emerald-50 text-emerald-700 border border-emerald-100 py-5 rounded-[2rem] font-black text-sm hover:bg-emerald-600 hover:text-white transition-all flex items-center justify-center gap-3 transform active:scale-95"
                >
                    <MessageCircle size={22} fill="currentColor" className="opacity-20" />
                    تأكيد عبر واتساب
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Column: Guidance */}
        <div className="lg:col-span-5 space-y-6">
          
          {/* Important Instructions Card */}
          <div className="bg-gray-900 rounded-[2.5rem] p-8 lg:p-10 text-white relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 right-0 w-40 h-40 bg-primary-600/20 rounded-full blur-3xl -z-0"></div>
            <div className="relative z-10">
                <h3 className="text-xl font-black mb-8 flex items-center gap-3">
                    <Info className="text-primary-400" />
                    تعليمات هامة للطلاب
                </h3>
                
                <div className="space-y-6">
                    {[
                        { title: 'وضوح الصورة', desc: 'تأكد من أن لقطة الشاشة واضحة وتظهر رقم العملية وتاريخ التحويل بالكامل.' },
                        { title: 'فترة المراجعة', desc: 'يقوم فريق الإدارة بمراجعة الطلبات يدوياً لضمان الأمان، وتستغرق المراجعة من 1-24 ساعة.' },
                        { title: 'كود التفعيل', desc: 'فور الموافقة، ستصلك رسالة إشعار وتنبيه على واتساب يحتوي على كود تفعيل الكورس الخاص بك.' },
                        { title: 'تتبع الطلب', desc: 'يمكنك متابعة حالة "قيد الانتظار" أو "تم القبول" من خلال صفحة اشتراكاتي في لوحة التحكم.' }
                    ].map((step, idx) => (
                        <div key={idx} className="flex items-start gap-4">
                            <div className="w-2 h-2 rounded-full bg-primary-500 mt-2 flex-shrink-0"></div>
                            <div>
                                <h4 className="text-sm font-black text-white">{step.title}</h4>
                                <p className="text-xs text-gray-400 mt-1 leading-relaxed">{step.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-10 pt-8 border-t border-white/5">
                    <div className="flex items-center gap-4 bg-white/5 p-4 rounded-2xl border border-white/10">
                        <AlertCircle className="text-amber-500 flex-shrink-0" size={24} />
                        <p className="text-[10px] font-bold text-gray-300 leading-snug uppercase tracking-tighter">
                            في حال واجهت أي صعوبة في الدفع أو تأخر التفعيل، يرجى مراسلة الدعم الفني فوراً.
                        </p>
                    </div>
                </div>
            </div>
          </div>

          {/* Verification Badge Card */}
          <div className="bg-primary-50 rounded-[2.5rem] p-8 border border-primary-100 flex flex-col items-center text-center">
             <div className="w-20 h-20 bg-white rounded-3xl shadow-lg flex items-center justify-center mb-4 text-primary-600 transform rotate-3">
                <CreditCard size={40} />
             </div>
             <h4 className="text-lg font-black text-gray-900">دفع آمن وموثوق</h4>
             <p className="text-xs text-gray-500 mt-2 max-w-xs leading-relaxed font-medium">
                جميع المعاملات المالية مشفرة وتتم مراجعتها من قبل مختصين لضمان حق الطالب والمدرس.
             </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PaymentConfirmation;